import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Copy, Sparkles, ExternalLink, Loader2 } from 'lucide-react';
import { fetchAvatarPresets } from '../../../api/virtualHuman.js';
import { SUPPORTED_LANGS } from '../../../i18n/index.js';

/**
 * VirtualHumanModal — 工作室「虛擬人」
 *   - 男/女預設虛擬人卡片選單（GET /avatar/presets）
 *   - 選一個 → 下方產生「獨立的虛擬人網址」/avatar?spaceId=&vh=<id>（可複製分享）
 *   - 該網址支援鍵盤 + 語音兩種模式。不寫進 avatar 設定，是獨立的分享連結。
 * 米色系 editorial 風，對齊 ShareLinkModal。
 */
const SHARE_BASE = 'https://ivr-web.qubby.io';

const overlay = { hidden: { opacity: 0 }, show: { opacity: 1 }, exit: { opacity: 0 } };
const panel = {
    hidden: { opacity: 0, y: 18, scale: 0.97 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: 10, scale: 0.985, transition: { duration: 0.18 } },
};

async function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
    } catch { return false; }
}

export function VirtualHumanModal({ open, onClose, avatarName, spaceId, defaultLanguage }) {
    const { t } = useTranslation('wsModals');
    const [selected, setSelected] = useState('');
    const [presets, setPresets] = useState({ male: [], female: [] });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const defLang = defaultLanguage || 'zh-TW';
    const [lang, setLang] = useState(defLang);
    const defLangLabel = (SUPPORTED_LANGS.find((l) => l.code === defLang) || {}).label || defLang;

    useEffect(() => {
        if (!open) return;
        setErr(''); setLoading(true); setLang(defLang);
        fetchAvatarPresets()
            .then((d) => {
                const female = d.female || [], male = d.male || [];
                setPresets({ male, female });
                // 預設選第一個（女→男），讓網址一開就有效
                setSelected((prev) => prev || female[0]?.id || male[0]?.id || '');
            })
            .catch((e) => setErr(e.message || 'load presets failed'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 選的語言 ≠ 角色預設 → 網址帶 &lang=（講該語系）；等於預設就不帶（用角色預設語系）
    const langQ = (lang && lang !== defLang) ? `&lang=${encodeURIComponent(lang)}` : '';
    const base = spaceId && selected ? `${SHARE_BASE}/avatar?spaceId=${spaceId}&vh=${selected}${langQ}` : '';
    const urlText = base;                       // 鍵盤預設
    const urlVoice = base ? `${base}&mode=voice` : ''; // 語音預設

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    variants={overlay} initial="hidden" animate="show" exit="exit"
                    onClick={onClose}
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 px-5 backdrop-blur-[3px]"
                >
                    <motion.div
                        variants={panel} onClick={(e) => e.stopPropagation()}
                        className="relative flex max-h-[88vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[22px] bg-cream-50 shadow-[0_36px_90px_-28px_rgba(28,26,23,0.5)] ring-1 ring-cream-200"
                    >
                        <button
                            onClick={onClose} title={t('common:actions.close')}
                            className="absolute right-5 top-5 z-10 inline-flex size-8 items-center justify-center rounded-full text-ink-300 transition-colors hover:bg-cream-100 hover:text-ink-900"
                        >
                            <X size={17} />
                        </button>

                        {/* header */}
                        <div className="shrink-0 px-7 pt-7 pb-4">
                            <div className="mb-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-accent-700">
                                <Sparkles size={12} strokeWidth={2.2} /> {t('vh.badge')}
                            </div>
                            <h2 className="heading-display text-[25px] font-semibold leading-[1.1] text-ink-900">{t('vh.title')}</h2>
                            <p className="mt-2 max-w-[92%] text-[13px] leading-relaxed text-ink-500">
                                {t('vh.descPrefix')}{' '}
                                <span className="font-medium text-ink-700">{avatarName || t('vh.thisAgent')}</span>
                                {' '}{t('vh.descSuffix')}
                            </p>
                        </div>

                        <div className="h-px bg-cream-200" />

                        <div className="min-h-0 flex-1 overflow-y-auto px-7 py-5">
                            {loading ? (
                                <div className="flex items-center justify-center gap-2 py-12 text-ink-400">
                                    <Loader2 size={16} className="animate-spin" /> <span className="text-[13px]">{t('vh.loading')}</span>
                                </div>
                            ) : err ? (
                                <div className="rounded-xl bg-danger-100/50 px-4 py-3 text-[13px] text-danger-500">{err}</div>
                            ) : (
                                <>
                                    <AvatarGroup title={t('vh.female')} emoji="👩" list={presets.female} selected={selected} onPick={setSelected} t={t} />
                                    <AvatarGroup title={t('vh.male')} emoji="👨" list={presets.male} selected={selected} onPick={setSelected} t={t} />
                                </>
                            )}
                        </div>

                        {/* footer: 獨立虛擬人網址 */}
                        {spaceId && (
                            <div className="shrink-0 space-y-3 border-t border-cream-200 px-7 py-4">
                                {/* 語系：預設 = 角色預設語系；可改成講不同語系的網址 */}
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-[12px] text-ink-500">
                                        {t('vh.langLabel')}
                                        <span className="ml-1.5 rounded bg-cream-100 px-1.5 py-0.5 font-mono text-[11px] text-ink-400">{defLangLabel}</span>
                                    </span>
                                    <select
                                        value={lang}
                                        onChange={(e) => setLang(e.target.value)}
                                        className="rounded-lg border border-cream-300 bg-cream-50 px-2.5 py-1.5 text-[12.5px] text-ink-900 focus:border-accent-400 focus:outline-none"
                                    >
                                        {SUPPORTED_LANGS.map((l) => (
                                            <option key={l.code} value={l.code}>
                                                {l.flag} {l.label}{l.code === defLang ? `（${t('vh.langDefaultTag')}）` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <UrlRow label={t('vh.urlKeyboard')} url={urlText} t={t} />
                                <UrlRow label={t('vh.urlVoice')} url={urlVoice} t={t} />
                                <p className="text-[11.5px] leading-relaxed text-ink-400">{t('vh.urlHint')}</p>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function UrlRow({ label, url, t }) {
    return (
        <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-300">{label}</div>
            <div className="flex items-stretch gap-2">
                <div className="flex min-w-0 flex-1 items-center rounded-xl border border-cream-200 bg-white px-3 py-2">
                    <span className="truncate font-mono text-[12px] text-ink-700">{url || '—'}</span>
                </div>
                <UrlCopyBtn url={url} t={t} />
                {url && (
                    <a href={url} target="_blank" rel="noreferrer" title={t('vh.openNewTab')}
                       className="inline-flex shrink-0 items-center justify-center rounded-xl border border-cream-200 bg-white px-2.5 text-ink-400 transition-colors hover:border-accent-300 hover:text-accent-600">
                        <ExternalLink size={15} />
                    </a>
                )}
            </div>
        </div>
    );
}

function AvatarGroup({ title, emoji, list, selected, onPick, t }) {
    if (!list?.length) return null;
    return (
        <div className="mb-4">
            <div className="mb-2 text-[12px] font-semibold text-ink-500">{emoji} {title}</div>
            <div className="grid grid-cols-3 gap-2.5">
                {list.map((a) => {
                    const on = selected === a.id;
                    return (
                        <button
                            key={a.id} type="button" onClick={() => onPick(a.id)}
                            className={['group relative flex flex-col items-center gap-1.5 rounded-2xl border p-2.5 transition-all',
                                on ? 'border-accent-400 bg-accent-50/50 ring-1 ring-accent-300' : 'border-cream-200 bg-white hover:border-accent-200'].join(' ')}
                        >
                            {on && (
                                <span className="absolute right-1.5 top-1.5 inline-flex size-4 items-center justify-center rounded-full bg-accent-500 text-cream-50">
                                    <Check size={11} strokeWidth={3} />
                                </span>
                            )}
                            <img src={a.imageUrl} alt={t(`vh.avatars.${a.nameKey}.name`)} loading="lazy"
                                 className="h-20 w-full rounded-xl object-cover bg-cream-100" />
                            <span className="text-[12.5px] font-semibold text-ink-900">{t(`vh.avatars.${a.nameKey}.name`)}</span>
                            <span className="text-center text-[10px] leading-tight text-ink-400">{t(`vh.avatars.${a.descKey}.desc`)}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function UrlCopyBtn({ url, t }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            disabled={!url}
            onClick={async () => { if (url && await copyText(url)) { setCopied(true); setTimeout(() => setCopied(false), 1800); } }}
            className={['inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3.5 text-[12px] font-medium transition-all active:scale-95 disabled:opacity-50',
                copied ? 'bg-success-500 text-cream-50' : 'bg-accent-500 text-cream-50 hover:bg-accent-700'].join(' ')}
        >
            {copied ? <><Check size={14} /> {t('vh.copied')}</> : <><Copy size={14} /> {t('vh.copy')}</>}
        </button>
    );
}
